import React, { useState, useEffect } from 'react';
import {
  Box, Container, Grid, Paper, List, ListItem, ListItemIcon, ListItemText,
  Avatar, Typography, IconButton, Tooltip, CircularProgress, useTheme, alpha,
  Drawer, useMediaQuery, Button
} from '@mui/material';
import {
  Person, History, EmojiEvents, BarChart, Settings,
  PhotoCamera, Menu, Star, CardGiftcard
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { uploadProfilePhoto, getUserHistory } from '../../api/user';
import EcoStats from './EcoStats';
import RecentClassifications from './RecentClassifications';
import RewardsLeaderboard from './RewardsLeaderboard';
import ProfileDetails from './ProfileDetails';
import ProfileSettings from './ProfileSettings';
import FeedbackStats from './FeedbackStats';
import RecentRewards from './RecentRewards';

// Animated Background Component
const PlayableBackground = () => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        background: `
          radial-gradient(circle at 20% 80%, rgba(67,160,71,0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255,196,0,0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(0,184,148,0.1) 0%, transparent 50%),
          linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)
        `,
        backgroundSize: '200% 200%',
        animation: 'gradientShift 20s ease infinite',
        '@keyframes gradientShift': {
          '0%': { backgroundPosition: '0% 50%' },
          '50%': { backgroundPosition: '100% 50%' },
          '100%': { backgroundPosition: '0% 50%' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2343a047' fill-opacity='0.03'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
          `,
          animation: 'float 25s ease-in-out infinite',
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-15px)' },
        },
      }}
    />
  );
};

const ProfileLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoadingHistory(true);
        const response = await getUserHistory();
        setHistory(response.data);
      } catch (error) {
        console.error("Failed to fetch classification history:", error);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, []);

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: <Person />, color: '#43a047' },
    { id: 'stats', label: 'Eco Stats', icon: <Star />, color: '#66bb6a' },
    { id: 'history', label: 'Classifications', icon: <History />, color: '#00b894' },
    { id: 'rewards', label: 'Rewards & Ranks', icon: <EmojiEvents />, color: '#ffc400' },
    { id: 'recent-rewards', label: 'Recent Rewards', icon: <CardGiftcard />, color: '#9c27b0' },
    { id: 'analytics', label: 'Analytics', icon: <BarChart />, color: '#03a9f4' },
    { id: 'settings', label: 'Settings', icon: <Settings />, color: '#e53935' },
  ];

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadProfilePhoto(file);
      const newPhotoPath = response.data.profilePhotoPath + '?t=' + Date.now();
      setUser(prev => ({ ...prev, profilePhotoPath: newPhotoPath }));
    } catch (error) {
      console.error('Photo upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <ProfileDetails user={user} setUser={setUser} />;
      case 'stats':
        return <EcoStats user={user} history={history} />;
      case 'history':
        return <RecentClassifications history={history} loading={loadingHistory} />;
      case 'rewards':
        return <RewardsLeaderboard />;
      case 'recent-rewards':
        return <RecentRewards />;
      case 'analytics':
        return <FeedbackStats />;
      case 'settings':
        return <ProfileSettings />;
      default:
        return <ProfileDetails user={user} setUser={setUser} />;
    }
  };

  const sideMenu = (
    <Box sx={{ width: 280, height: '100%' }}>
      {/* Profile Header */}
      <Box sx={{
        p: 3,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
        position: 'relative',
        minHeight: '240px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '20px',
          background: `linear-gradient(135deg, transparent, ${alpha('#ffffff', 0.1)})`
        }
      }}>
          <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
              {/* Avatar (relative wrapper for absolute positioning inside) */}
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar
                      src={user?.profilePhotoPath || ""}
                      alt={user?.displayName || "User"}
                      sx={{
                          width: 100,
                          height: 100,
                          border: '4px solid white',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                          mx: 'auto'
                      }}
                  />
                  <Tooltip title="Change Photo">
                      <IconButton
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          sx={{
                              position: 'absolute',
                              top: -8, // Pulls the icon up to overlap the border
                              right: -8, // Pulls the icon in to overlap the border
                              backgroundColor: 'white',
                              boxShadow: 3,
                              width: 32,
                              height: 32,
                              zIndex: 2,
                              '&:hover': { backgroundColor: '#f5f5f5', transform: 'scale(1.1)' }
                          }}
                      >
                          {uploading ? <CircularProgress size={16} /> : <PhotoCamera sx={{ fontSize: 16 }} />}
                      </IconButton>
                  </Tooltip>
              </Box>
              <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
              />
          </Box>

          <Typography variant="h5" sx={{
          color: 'white',
          fontWeight: 800,
          textShadow: '0 2px 8px rgba(0,0,0,0.3)',
          mb: 1
        }}>
          {user?.displayName || "User"}
        </Typography>
        <Box sx={{
          color: 'rgba(100,0,0,0.95)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.1))',
          borderRadius: '20px',
          padding: '8px 16px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <Box component="span" sx={{ fontSize: '1.2rem' }}>⭐</Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {user?.rewardsPoints || 0} Champion Points
          </Typography>
        </Box>
      </Box>

      {/* Menu Items */}
      <List sx={{ p: 2, backgroundColor: 'transparent' }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.id}
            button
            onClick={() => {
              setActiveTab(item.id);
              if (isMobile) setDrawerOpen(false);
            }}
            sx={{
              borderRadius: 3,
              mb: 1,
              background: activeTab === item.id
                ? `linear-gradient(135deg, ${item.color}, ${alpha(item.color, 0.8)})`
                : 'transparent',
              color: activeTab === item.id ? 'white' : '#1b5e20',
              '&:hover': {
                background: activeTab === item.id
                  ? `linear-gradient(135deg, ${item.color}, ${alpha(item.color, 0.8)})`
                  : alpha(item.color, 0.1),
                transform: 'translateX(8px)'
              },
              transition: 'all 0.3s ease',
              boxShadow: activeTab === item.id ? `0 4px 20px ${alpha(item.color, 0.3)}` : 'none'
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: activeTab === item.id ? 700 : 600,
                fontSize: '1rem'
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={80} sx={{ color: '#43a047' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative' }}>
      <PlayableBackground />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Desktop Sidebar */}
          {!isMobile && (
            <Grid item md={3}>
              <Paper elevation={8} sx={{
                borderRadius: 4,
                overflow: 'hidden',
                backdropFilter: 'blur(20px)',
                background: alpha('#43a047', 0.15),
                border: `2px solid ${alpha('#43a047', 0.1)}`
              }}>
                {sideMenu}
              </Paper>
            </Grid>
          )}

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {isMobile && (
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Menu />}
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    borderColor: '#43a047',
                    color: '#43a047',
                    '&:hover': { backgroundColor: '#43a04710' }
                  }}
                >
                  Profile Menu
                </Button>
              </Box>
            )}

            {renderContent()}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              background: alpha('#43a047', 0.15),
              backdropFilter: 'blur(20px)'
            }
          }}
        >
          {sideMenu}
        </Drawer>
      )}
    </Box>
  );
};

export default ProfileLayout;
